// web/src/api/quality/rule-sets.ts —— 规则集（用途 / CRUD / 复制 / 成员与未归属查询）
// 拆自 web/src/api/quality.ts 的同名分区（内容原样搬运）；由 web/src/api/quality.ts 以 export * 聚合，导出面对调用方不变。

import { del, get, post, put } from '../../request';
import { toPage, type Page, type PageQuery } from '../types';
import type { TranscriptSegment } from '../cdr';

import type { QualityRule } from './rules';

// ---------- 规则集（按 id 关联，名称作系统内部稳定引用） ----------

/** 规则集用途：offline=任务（离线批量） / realtime=实时（仅四类规则生效） / flow=流程（工单） */
export type RuleSetUsage = 'offline' | 'realtime' | 'flow';

export interface RuleSet {
  id: number;
  name: string;
  description?: string;
  /** JSON 数组字符串：["offline","realtime","flow"] */
  usage?: string;
  enabled?: boolean;
  is_system?: boolean;
  /** 该集合下启用的规则数 */
  member_count: number;
  /** 该集合下全部规则数（含停用）；0=尚未应用任何模板规则 */
  rule_count: number;
  /** 该集合下违规级别=致命的规则数（红线：命中即该段总分归零，不参与加权平均） */
  fatal_count?: number;
  /** 该集合下「启用且非致命」的规则数：0 且 rule_count>0 = 纯红线集合，模板里引用但不计权重 */
  scored_count?: number;
  [k: string]: any;
}

export interface RuleSetListData {
  list: RuleSet[];
  /** 无规则集归属（rule_set_id 为空）的启用规则数 */
  uncategorized: number;
}

/** GET /quality/rule-sets */
export async function listRuleSets(): Promise<RuleSetListData> {
  const data: any = await get('/quality/rule-sets');
  return { list: (data?.list || []) as RuleSet[], uncategorized: Number(data?.uncategorized || 0) };
}

/** POST /quality/rule-sets（新建；名称必填且唯一，enabled 缺省为 true） */
export function createRuleSet(body: {
  name: string;
  description?: string;
  usage: RuleSetUsage[];
  /** 归属组织 id；0=省级共享（可被所有地域引用，并作为兜底） */
  org_id?: number;
  /** 适用业务类型 code 数组（如 ['collection']）；空=不限 */
  business_types?: string[];
  enabled?: boolean;
}) {
  return post('/quality/rule-sets', body);
}

/** PUT /quality/rule-sets/:id（部分更新） */
export function updateRuleSet(id: number, body: { name?: string; description?: string; usage?: RuleSetUsage[]; org_id?: number; business_types?: string[]; enabled?: boolean }) {
  return put(`/quality/rule-sets/${id}`, body);
}

/** DELETE /quality/rule-sets/:id（有成员或在途任务引用时后端拒绝） */
export function deleteRuleSet(id: number) {
  return del(`/quality/rule-sets/${id}`);
}

/**
 * POST /quality/rule-sets/:id/copy —— 单条复制到目标组织（连同成员规则）。
 * 名称留空=沿用原名（规则集名称全局唯一，跨组织复制通常要改名，后端会因重名报错）。
 */
export function copyRuleSet(id: number, body: { target_org_id: number; name?: string }) {
  return post(`/quality/rule-sets/${id}/copy`, body);
}

/** 批量复制单条结果（failed 的条目带 error，允许部分成功） */
export interface RuleSetCopyResult {
  source_id: number;
  id?: number;
  name?: string;
  rule_count?: number;
  error?: string;
}

/**
 * POST /quality/rule-sets/batch-copy —— 一次把多个规则集连同成员规则复制到目标组织。
 * 名称默认「<原名>（<目标组织名>）」，重名自动加序号；逐条返回结果，允许部分成功。
 */
export function batchCopyRuleSets(body: { ids: number[]; target_org_id: number; name_suffix?: string }): Promise<{
  results: RuleSetCopyResult[];
  ok: number;
  failed: number;
}> {
  return post('/quality/rule-sets/batch-copy', body) as Promise<{ results: RuleSetCopyResult[]; ok: number; failed: number }>;
}

/** GET /quality/rule-sets/:id/rules（集合成员规则，含停用） */
export async function listRuleSetMembers(id: number): Promise<QualityRule[]> {
  const data: any = await get(`/quality/rule-sets/${id}/rules`);
  return (data?.list || []) as QualityRule[];
}

/** GET /quality/rule-sets/uncategorized-rules（未归属任何规则集的启用规则） */
export async function listUncategorizedRules(): Promise<QualityRule[]> {
  const data: any = await get('/quality/rule-sets/uncategorized-rules');
  return (data?.list || []) as QualityRule[];
}

/** 解析规则集用途 JSON 字符串 → 用途数组 */
export function parseRuleSetUsage(usage?: string): RuleSetUsage[] {
  if (!usage) return [];
  try {
    const arr = JSON.parse(usage);
    return Array.isArray(arr) ? (arr as RuleSetUsage[]) : [];
  } catch {
    return [];
  }
}

export const RULE_SET_USAGE_LABEL: Record<RuleSetUsage, string> = {
  offline: '任务',
  realtime: '实时',
  flow: '流程',
};

/** 解析模板/规则 business_types JSON → 业务类型 code 数组 */
export function parseBusinessTypes(bt?: string): string[] {
  if (!bt) return [];
  if (Array.isArray(bt)) return bt as string[];
  if (typeof bt === 'string') {
    try {
      const arr = JSON.parse(bt);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** 导入单条结果（允许部分成功：一条失败不影响其余） */
export interface RuleSetImportItem {
  source_name?: string;
  id?: number;
  new_name?: string;
  rule_count?: number;
  error?: string;
}

export interface RuleSetImportResult {
  total: number;
  ok: number;
  failed: number;
  items: RuleSetImportItem[];
}

/**
 * POST /quality/rule-sets/import —— 导入规则集导出文件，跨环境同步规则内容。
 * 总是新建副本（不匹配同名、不覆盖既有内容），集合名自动加「（导入）」并去重；
 * data 是导出文件的 JSON 对象，target_org_id 决定新集合的归属组织（0=省级共享）。
 */
export function importRuleSets(body: { target_org_id: number; data: unknown }): Promise<RuleSetImportResult> {
  return post('/quality/rule-sets/import', body) as Promise<RuleSetImportResult>;
}

/** 解析 JSON 数组字符串为 id 数组（组织/技能组维度；空/非法返回 []） */
export function parseIDArray(raw?: string): number[] {
  if (!raw) return [];
  let arr: unknown = raw;
  if (typeof raw === 'string') {
    try {
      arr = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) return [];
  return arr.map((v) => Number(v)).filter((n) => Number.isFinite(n));
}

